import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutGrid, StickyNote, FileEdit, CheckSquare, FolderArchive, Quote, Settings,
  LogOut, ExternalLink, Sparkles, Briefcase, Calendar, Activity, Menu, X, Palette,
} from "lucide-react";
import { Switch } from "../../components/ui/switch";
import { api } from "../../lib/api";
import { useAdmin } from "../admin/AdminApp";
import NotificationBell from "../../components/admin/NotificationBell";
import MessagesChatBubble from "../../components/admin/MessagesChatBubble";

/**
 * Private workspace shell. Lives at /admin/workspace/*.
 * Fully responsive: desktop sidebar (md+), mobile hamburger drawer, compact
 * icon-only buttons on narrow phones (Samsung Fold closed ≈ 344 px).
 */

const NAV = [
  ["", "Panel", LayoutGrid],
  ["talepler", "Aksiyon Takibi", Activity],
  ["notlar", "Notlar", StickyNote],
  ["yazilar", "Yazılarım", FileEdit],
  ["gorevler", "To-Do", CheckSquare],
  ["belgeler", "Belgelerim", FolderArchive],
  ["alintilar", "Alıntılarım", Quote],
  ["takvim", "Takvim", Calendar],
  ["post-editor", "Post Editörü", Palette],
  ["nova", "NovaAI", Sparkles],
  ["ayarlar", "Ayarlar", Settings],
];

function SwitchModeToggle({ compact = false }) {
  const nav = useNavigate();
  const loc = useLocation();
  const inWorkspace = loc.pathname.startsWith("/admin/workspace");
  const flip = (v) => nav(v ? "/admin/workspace" : "/admin");
  if (compact) {
    // Compact pill — only shows the ACTIVE mode name + a tap to flip.
    return (
      <button
        onClick={() => flip(!inWorkspace)}
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full border border-indigo-200 bg-white/80 text-indigo-700 hover:bg-indigo-50"
        data-testid="workspace-switch-compact"
        title="Mod değiştir"
      >
        <Sparkles size={11} className="text-amber-500" />
        Workspace
      </button>
    );
  }
  return (
    <label className="flex items-center gap-2 lg:gap-3 text-xs sm:text-sm">
      <span className={inWorkspace ? "text-stone-400 hidden lg:inline" : "font-semibold text-[#14213D] hidden lg:inline"}>
        <Briefcase size={14} className="inline mr-1 -mt-0.5" />Site
      </span>
      <Switch checked={inWorkspace} onCheckedChange={flip} data-testid="workspace-switch" />
      <span className={inWorkspace ? "font-semibold text-indigo-600" : "text-stone-400"}>
        <Sparkles size={14} className="inline mr-1 -mt-0.5" />Workspace
      </span>
    </label>
  );
}

function NavItems({ counts, onClick }) {
  return (
    <>
      {NAV.map(([to, label, Icon]) => (
        <NavLink
          key={to}
          to={`/admin/workspace/${to}`}
          end={to === ""}
          onClick={onClick}
          data-testid={`workspace-nav-${to || "dashboard"}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
              isActive
                ? "bg-white/15 text-white shadow-inner ring-1 ring-white/10"
                : "text-white/70 hover:bg-white/10 hover:text-white"
            }`
          }
        >
          <Icon size={17} />
          <span className="flex-1">{label}</span>
          {to === "talepler" && counts?.total > 0 && (
            <span className="text-[10px] bg-red-500 text-white rounded-full px-1.5 py-0.5" data-testid="ws-inbox-badge">{counts.total}</span>
          )}
        </NavLink>
      ))}
    </>
  );
}

export default function WorkspaceLayout() {
  const { logout, user } = useAdmin();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const loc = useLocation();

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);
  // Lock body scroll while drawer open
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  const { data: counts } = useQuery({
    queryKey: ["inbox", "counts"],
    queryFn: () => api.get("/admin/inbox/counts").then((r) => r.data),
    refetchInterval: 30000,
  });

  const sidebarGrad = { background: "linear-gradient(180deg, #1e1b4b 0%, #312e81 55%, #4c1d95 100%)" };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/40 to-violet-50/60">
      {/* ---- Desktop sidebar ---- */}
      <aside
        className="w-64 shrink-0 hidden md:flex flex-col sticky top-0 h-screen text-white/90"
        style={sidebarGrad}
        data-testid="workspace-sidebar"
      >
        <div className="px-6 h-16 flex items-center gap-2 font-signature text-2xl text-white">
          <Sparkles size={22} className="text-amber-300" /> Workspace
        </div>
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          <NavItems counts={counts} />
        </nav>
        <div className="p-4 border-t border-white/10 text-xs text-white/50 truncate">{user.email}</div>
      </aside>

      {/* ---- Mobile drawer (slide-in from left) ---- */}
      {drawerOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/50 z-40 animate-in fade-in"
            onClick={() => setDrawerOpen(false)}
            data-testid="workspace-drawer-overlay"
          />
          <aside
            className="md:hidden fixed left-0 top-0 bottom-0 w-72 max-w-[80vw] z-50 flex flex-col text-white/90 shadow-2xl animate-in slide-in-from-left"
            style={sidebarGrad}
            data-testid="workspace-drawer"
          >
            <div className="px-5 h-14 flex items-center gap-2 font-signature text-xl text-white border-b border-white/10">
              <Sparkles size={20} className="text-amber-300" />
              <span className="flex-1">Workspace</span>
              <button onClick={() => setDrawerOpen(false)} className="p-1 rounded hover:bg-white/10" aria-label="Kapat">
                <X size={18} />
              </button>
            </div>
            <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
              <NavItems counts={counts} onClick={() => setDrawerOpen(false)} />
            </nav>
            <div className="p-3 border-t border-white/10 space-y-2">
              <div className="text-[11px] text-white/50 truncate px-1">{user.email}</div>
              <div className="flex items-center gap-2">
                <SwitchModeToggle />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <a href="/" target="_blank" rel="noopener noreferrer" className="flex-1 text-xs text-white/70 hover:text-white inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-white/10">
                  <ExternalLink size={12} /> Siteyi gör
                </a>
                <button onClick={logout} className="flex-1 text-xs text-rose-200 hover:text-white inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-white/10 hover:bg-red-600/40">
                  <LogOut size={12} /> Çıkış
                </button>
              </div>
            </div>
          </aside>
        </>
      )}

      {/* ---- Main area ---- */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 sm:h-16 bg-white/80 backdrop-blur-md border-b border-indigo-100 flex items-center gap-2 px-2 sm:px-4 lg:px-8 sticky top-0 z-30">
          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 -ml-1 rounded-lg hover:bg-indigo-50 text-stone-700"
            onClick={() => setDrawerOpen(true)}
            aria-label="Menü"
            data-testid="workspace-mobile-menu"
          >
            <Menu size={20} />
          </button>

          {/* Mobile: compact logo | Desktop: mode switch */}
          <div className="md:hidden flex items-center gap-1.5 font-signature text-base text-indigo-700 truncate">
            <Sparkles size={16} className="text-amber-500 shrink-0" />
            <span className="truncate">Workspace</span>
          </div>
          <div className="hidden md:block">
            <SwitchModeToggle />
          </div>

          <div className="flex-1" />

          {/* Right-side actions — collapse into icons on narrow screens */}
          <div className="flex items-center gap-1 sm:gap-2">
            <NotificationBell />
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:inline-flex text-sm items-center gap-1.5 text-stone-600 hover:text-stone-900 px-2 py-1 rounded hover:bg-stone-100"
              title="Siteyi gör"
            >
              <ExternalLink size={15} /> <span className="hidden xl:inline">Siteyi gör</span>
            </a>
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="lg:hidden p-2 rounded-lg hover:bg-stone-100 text-stone-600"
              title="Siteyi gör"
              aria-label="Siteyi gör"
            >
              <ExternalLink size={16} />
            </a>
            <button
              onClick={logout}
              className="hidden sm:inline-flex text-sm items-center gap-1.5 text-stone-600 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50"
              title="Çıkış"
            >
              <LogOut size={15} /> <span className="hidden lg:inline">Çıkış</span>
            </button>
            <button
              onClick={logout}
              className="sm:hidden p-2 rounded-lg hover:bg-red-50 text-stone-600 hover:text-red-600"
              title="Çıkış"
              aria-label="Çıkış"
            >
              <LogOut size={16} />
            </button>
          </div>
        </header>

        <main className="flex-1 p-3 sm:p-5 lg:p-8 min-w-0 max-w-full">
          <div className="max-w-full xl:max-w-[1600px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      <MessagesChatBubble hideOnPaths={["/admin/workspace/talepler"]} />
    </div>
  );
}

export function WsPageTitle({ title, sub, action, icon: Icon }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-4 sm:mb-6">
      <div className="flex items-start gap-2.5 sm:gap-3 min-w-0 flex-1">
        {Icon && (
          <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0 text-white shadow-md"
               style={{ background: "linear-gradient(135deg,#6366f1 0%,#8b5cf6 100%)" }}>
            <Icon size={18} className="sm:w-5 sm:h-5" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 leading-tight break-words" data-testid="ws-page-title">{title}</h1>
          {sub && <p className="text-stone-500 text-xs sm:text-sm mt-0.5 sm:mt-1">{sub}</p>}
        </div>
      </div>
      {action && <div className="w-full sm:w-auto flex items-center gap-2 flex-wrap">{action}</div>}
    </div>
  );
}
